import { getTopicsByLevel } from './topics';
import { fetchExamsByLevel } from './exams';
import { getUserTopicContentProgresses, getUserTopicExamProgresses } from './progresses';

// How long a prefetched level's sidebar content is trusted before a fresh fetch is required --
// protects against serving stale data if content changes between prefetch and the user actually
// clicking into the level. Also bounds how long a learner's OWN progress can read back stale
// after they complete something -- see loadLevelSidebarContent's docstring and the
// invalidateLevelSidebarContent calls in Level.jsx's progress-sync effects, which shrink that
// window to ~0 for the common case (completing something, then revisiting this same level).
const CACHE_TTL_MS = 60000;

// Keyed by level id -> { data, timestamp } once resolved, or a Promise while in flight (so
// concurrent callers for the same level -- e.g. hovering a card that's already being prefetched
// from a previous hover -- share one fetch instead of issuing duplicates).
const cache = new Map();

/**
 * Fetch what Level.jsx's sidebar needs: topics and exams for one level, plus the caller's own
 * UserTopicContentProgress/UserTopicExamProgress records -- the latter two endpoints aren't
 * level-scoped server-side (they return the user's entire platform-wide progress history
 * regardless of levelId), so this pays that full cost once per cache entry rather than scoping
 * it down; the point of bundling them in here is to pay it at most once per CACHE_TTL_MS window
 * instead of on every single level visit. The progress halves are bundled in here -- rather than
 * fetched by Level.jsx on its own, uncached -- specifically so hovering a level card (see
 * DashboardGrid.jsx's prefetchLevelSidebarContent call) warms lock state too, not just the
 * topic/exam list: on a cache hit, isLocked is then correct from the very first paint with no
 * fetch at all. Deliberately does NOT include chapters/tests/topic-contents/lessons -- that
 * heavier, level-wide batch is fetched separately by Level.jsx itself and is not part of the
 * hover-prefetch.
 * @param {number} levelId
 */
export async function loadLevelSidebarContent(levelId) {
    const [topics, exams, userTopicContentProgresses, userTopicExamProgresses] = await Promise.all([
        getTopicsByLevel(levelId),
        fetchExamsByLevel(levelId),
        getUserTopicContentProgresses(),
        getUserTopicExamProgresses(),
    ]);
    return { topics, exams, userTopicContentProgresses, userTopicExamProgresses };
}

/**
 * Returns the cached sidebar content for a level if present and still fresh, else null. Never
 * triggers a fetch itself.
 * @param {number} levelId
 */
export function getCachedLevelSidebarContent(levelId) {
    const entry = cache.get(levelId);
    if (!entry || entry instanceof Promise) return null;
    if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
        cache.delete(levelId);
        return null;
    }
    return entry.data;
}

/**
 * Fetches and caches a level's sidebar content if it isn't already cached-and-fresh (or already
 * in flight). Fire-and-forget friendly -- callers that don't need the result can ignore the
 * returned promise.
 * @param {number} levelId
 */
export function prefetchLevelSidebarContent(levelId) {
    const existing = cache.get(levelId);
    if (existing instanceof Promise) return existing;
    if (existing && Date.now() - existing.timestamp <= CACHE_TTL_MS) return Promise.resolve(existing.data);

    const promise = loadLevelSidebarContent(levelId).then(data => {
        cache.set(levelId, { data, timestamp: Date.now() });
        return data;
    }).catch(error => {
        cache.delete(levelId);
        throw error;
    });
    cache.set(levelId, promise);
    return promise;
}

/**
 * Drops a level's cached sidebar content, if present. Call after a topic/exam edit, or after a
 * learner's own progress-sync effect actually persists a change (see Level.jsx), made while
 * already on the level page -- so a subsequent revisit within the TTL doesn't serve back stale
 * pre-edit/pre-completion data.
 * @param {number} levelId
 */
export function invalidateLevelSidebarContent(levelId) {
    cache.delete(levelId);
}
